import { useEffect, useRef, type ReactNode } from "react";
import type { AccountBanner } from "../../types/transaction";

/** Which banner to bring into view; `key` moves on every refusal, so a repeat scrolls again. */
export interface BannerHighlight {
  last6: string;
  key: number;
}

interface ReviewBannersProps {
  banners: AccountBanner[];
  highlight: BannerHighlight | null;
}

/** True for the held banner the highlight names (any held banner when it names no last6). */
function isHighlighted(banner: AccountBanner, highlight: BannerHighlight | null): boolean {
  if (!highlight || banner.kind !== "held") return false;
  return highlight.last6 === "" || banner.last6 === highlight.last6;
}

/**
 * The banners above the review list, each the server's `message` verbatim.
 * A highlighted banner is scrolled into view and marked, so a held row's
 * refusal points at the sentence already on screen instead of repeating it.
 */
export function ReviewBanners({ banners, highlight }: ReviewBannersProps): ReactNode {
  const highlighted = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (highlight) highlighted.current?.scrollIntoView?.({ behavior: "smooth", block: "center" });
  }, [highlight]);

  if (banners.length === 0) return null;
  return (
    <div className="review-banners">
      {banners.map((banner) => {
        const marked = isHighlighted(banner, highlight);
        return (
          <div
            key={`${banner.kind}-${banner.last6}`}
            ref={marked ? highlighted : undefined}
            className={marked ? "review-banner highlighted" : "review-banner"}
            data-highlighted={marked ? "true" : undefined}
          >
            <p>{banner.message}</p>
          </div>
        );
      })}
    </div>
  );
}
